// ./src/components/layouts/DashboardLayout/DashboardLayout.tsx
import React, { useState, useCallback, useMemo, useEffect, useRef } from "react";
import { AppShell, Group, Text, Box, ActionIcon, Tooltip, Title, Indicator, TextInput, Menu, Button, UnstyledButton, useMantineTheme } from "@mantine/core";
import { StackedDrawer } from "@em/ui";
import { useDisclosure, useWindowScroll, useMediaQuery } from "@mantine/hooks";
import { Navigate, useNavigate, useLocation } from "react-router-dom";
import { useRedeemerOnly } from "@/hooks/useRedeemerOnly";
import { redeemerRedirect } from "@/utils/crewLanding";
import { useTranslation } from "react-i18next";
import { AnimatePresence, motion } from "framer-motion";

import { SiteMapItem, filterNavByVisibility, markEventFeatureNav, activeNavItem, EventFeatureNavCtx } from "@/config";

import { canUserAccess } from "@/utils/userRoleHelpers";
import { buildUrlWithFilters } from "@/utils/filterHelpers";

import { useApp } from "@/contexts/AppContext";
import { useBranding } from "@/contexts/BrandingContext";
import { useEntityFiltersContext } from "@/contexts/EntityFiltersContext";
import { useTagsContext } from "@/contexts/TagsContext";
import { useAccountPreferenceContext } from "@/contexts/AccountPreferenceContext";
import { useAppEnvironment } from "@/contexts/AppEnvironmentContext";
import { useCurrentEvent } from "@/contexts/CurrentEventContext/CurrentEventContext";
import { useCrewAccess } from "@/contexts/CrewAccessContext/CrewAccessContext";
import { CrewSessionService } from "@/utils/api/CrewSessionService";
import { useKeyboardShortcuts, useFeatureGate } from "@/hooks";
import { AppIcon } from "@/components/generic/AppIcon";
import { NotificationBell } from "@/components/features/Notifications";
import { SectionTabs } from "@/components/generic/SectionTabs";
import { ActionSheet } from "@/components/generic/ActionSheet";
import { NoAccessMessage } from "@/components/generic/NoAccessMessage";

import { SupportDrawer } from "@/components/features/Support/SupportDrawer";
import { CrewMessagesLauncher } from "@/components/features/CrewMessages";

import { DesktopSidebar } from "./DesktopSidebar";
import { BottomTabBar } from "./BottomTabBar";
import { MoreView } from "./MoreView";
import { SubscriptionFooter, useSubscriptionFooter } from "./SubscriptionFooter";
import { PWAInstallBanner } from "./PWAInstallBanner";
import { PWAUpdateBanner } from "./PWAUpdateBanner";

import {
  FooterMessageType,
  SIDEBAR_WIDTH,
  HEADER_HEIGHT,
  DEFAULT_ICON_COLOR,
  getIconBgColor,
} from "./constants";

// ============================================
// Types
// ============================================

/** A single item inside a header action's dropdown menu. */
export interface HeaderActionMenuItem {
  /** Item label */
  label: string;
  /** Optional Tabler icon name */
  iconName?: string;
  /** Optional item color (e.g. "red" for a destructive action) */
  color?: string;
  /** Whether the item is disabled */
  disabled?: boolean;
  /** Click handler */
  onClick: () => void;
}

export interface MobileHeaderAction {
  /** Tabler icon name */
  iconName: string;
  /** Tooltip text */
  tooltip?: string;
  /** Button variant — controls styling */
  variant?: "default" | "subtle" | "filled";
  /** Whether action is disabled */
  disabled?: boolean;
  /** Click handler (ignored when `menuItems` is provided). */
  onClick?: () => void;
  /**
   * When provided, the action renders as a dropdown menu (a kebab-style
   * trigger) instead of a plain button. `onClick` is ignored.
   */
  menuItems?: HeaderActionMenuItem[];
}

/** The dropdown body for a header action that carries `menuItems`. */
const HeaderActionMenuItems: React.FC<{ items: HeaderActionMenuItem[] }> = ({
  items,
}) => (
  <Menu.Dropdown>
    {items.map((item, i) => (
      <Menu.Item
        key={`${item.label}-${i}`}
        color={item.color}
        disabled={item.disabled}
        leftSection={
          item.iconName ? <AppIcon iconName={item.iconName} size={16} /> : undefined
        }
        onClick={item.onClick}
      >
        {item.label}
      </Menu.Item>
    ))}
  </Menu.Dropdown>
);

/**
 * A debounced search box rendered in the app header (top-right on desktop, a
 * slim bar on mobile). Provide this instead of a per-page search toolbar.
 */
export interface HeaderSearchConfig {
  /** Placeholder text. */
  placeholder?: string;
  /** Called (debounced) with the trimmed keyword as the user types. */
  onSearch: (keyword: string) => void;
  /** Initial value. */
  initialValue?: string;
}

/**
 * A "Filters" button rendered in the MOBILE header only (before the add button),
 * with a badge showing how many filters are applied. On larger screens the page
 * shows its filter bar inline instead — so pass this alongside an inline bar and
 * only the phone header surfaces the button. Tapping calls `onOpen` (the page
 * opens its `FilterDrawer`).
 */
export interface HeaderFilterConfig {
  /** Number of applied filters — shown as a badge; 0 hides it. */
  activeCount: number;
  /** Open the filter drawer. */
  onOpen: () => void;
  /** Tooltip / aria label. */
  label?: string;
}

// ============================================
// Account ⇄ Event workspace scoping
// ============================================

const EVENT_PATH_RE = /^\/events\/([^/]+)(?:\/|$)/;

// Account-scoped routes that live under /events/* but are NOT an event id.
const NON_EVENT_ID_SEGMENTS = new Set(["add", "all", "templates"]);

/** Extract the active :eventId from the path, or null when in the account workspace. */
const matchEventId = (pathname: string): string | null => {
  const m = pathname.match(EVENT_PATH_RE);
  if (!m) return null;
  const seg = m[1];
  // /events/all (list), /events/templates and /events/add stay account-scoped.
  return seg && !NON_EVENT_ID_SEGMENTS.has(seg) ? seg : null;
};

/** Deep-replace ":eventId" in a nav item's url (and its children) with a real id. */
const withEventId = (item: SiteMapItem, eventId: string): SiteMapItem => ({
  ...item,
  url: item.url ? item.url.replace(":eventId", eventId) : item.url,
  items: item.items?.map((child) => withEventId(child, eventId)),
});

interface DashboardLayoutProps {
  children: React.ReactNode;
  page: any;
  // page: {
  //   name?: string;
  //   subtitle?: string;
  //   icon?: string;
  //   iconColor?: string;
  //   roles?: string[];
  //   translationKey?: string;
  // };
  footerMessage?: string;
  footerMessageType?: FooterMessageType;
  showFooterMessage?: boolean;

  /** Action buttons displayed in the header (right side, desktop + mobile) */
  mobileHeaderActions?: MobileHeaderAction[];
  /** See `PageChromeValue.focusMode`. */
  focusMode?: boolean;

  /** Optional search box rendered in the header (replaces a page toolbar). */
  headerSearch?: HeaderSearchConfig;

  /**
   * Override the header title (normally the sitemap page name). Lets a page show
   * a shorter/contextual title — e.g. "Sub-events" instead of "Schedule" — to
   * free up space for header actions.
   */
  headerTitle?: string;

  /**
   * Override the header subtitle. Pass "" to hide the sitemap subtitle entirely
   * (e.g. when the page shows a live count in its place).
   */
  headerSubtitle?: string;

  /** Optional "Filters" button in the MOBILE header (badge + opens a drawer). */
  headerFilter?: HeaderFilterConfig;

  /**
   * Optional page-level action bar pinned to the bottom of the content area
   * (e.g. an editor's "Save changes" bar). Rendered via AppShell.Footer so it is
   * automatically fixed, spans the main area (offset by the sidebar), and the
   * content is padded so nothing hides behind it. On phones it floats above the
   * bottom tab bar. Provide a node only while it should be shown.
   */
  stickyFooter?: React.ReactNode;

  /**
   * Which page published the chrome above (ROADMAP A2b #58).
   *
   * ⚠⚠ THE SHELL OUTLIVES THE PAGE NOW, AND SOME OF ITS STATE MUST NOT. Everything in this
   * component used to be discarded on every navigation because the whole layout was thrown away
   * with the page; hoisting it above the route made that state PERSISTENT overnight. Most of it
   * should persist (an expanded sidebar, the scroll listener). Two things must not:
   *   · the header SEARCH box — it would carry a keyword onto the next page and, 300ms later, hand
   *     that page's `onSearch` a word the host never typed there;
   *   · an open mobile ACTION SHEET — it holds the previous page's menu items.
   * ⚠ Keyed on the PUBLISHER, not the route: a URL-stage drawer is a different route rendering the
   * same page, and the keyword has to survive opening one. See `PageChromeContext`.
   */
  chromeOwnerId?: number;
}

// ============================================
// Constants
// ============================================

/** Height of the bottom tab bar + safe area + extra padding for content offset */
const BOTTOM_TAB_BAR_OFFSET = 90;

/** Visual height of the fixed BottomTabBar (excluding the safe-area inset). The
 *  mobile sticky save bar floats directly ABOVE it so both stay tappable. */
const BOTTOM_TAB_BAR_HEIGHT = 84;

/** Reserve for the mobile sticky save bar (so page content clears it too). */
const MOBILE_STICKY_FOOTER_HEIGHT = 68;

// ============================================
// Page Title Animation Variants
// ============================================

const titleEase = [0.22, 0.68, 0.35, 1] as const;

const titleVariants = {
  initial: {
    opacity: 0,
    y: -12,
    filter: "blur(3px)",
  },
  animate: {
    opacity: 1,
    y: 0,
    filter: "blur(0px)",
    transition: {
      duration: 0.3,
      ease: titleEase,
    },
  },
  exit: {
    opacity: 0,
    y: 10,
    filter: "blur(3px)",
    transition: {
      duration: 0.18,
      ease: titleEase,
    },
  },
};

// ============================================
// Main Component
// ============================================

export const DashboardLayout: React.FC<DashboardLayoutProps> = ({
  children,
  page,
  footerMessage,
  footerMessageType = "info",
  showFooterMessage = false,
  focusMode = false,
  mobileHeaderActions,
  headerSearch,
  headerTitle,
  headerSubtitle,
  headerFilter,
  stickyFooter,
  chromeOwnerId = 0,
}) => {
  const theme = useMantineTheme();
  const { t } = useTranslation(["common"]);
  const brandName = useBranding().brand.name;
  const { isMobile } = useAppEnvironment();
  const screenFocused = isMobile && focusMode;
  const { currentEvent, featureUsage, refreshFeatureUsage } = useCurrentEvent();
  const { isCrewMode, role: crewRole } = useCrewAccess();
  const { hasFeature } = useFeatureGate();
  // In crew mode the viewer's EFFECTIVE roles are their crew role (their event
  // guest_role), not their account role — so the nav + page-access checks gate the
  // event workspace by what the crew role may see (docs/CREW-ACCESS.md).
  const crewRoles = useMemo(() => (isCrewMode && crewRole ? [crewRole] : []), [isCrewMode, crewRole]);
  const hasStickyFooter = Boolean(stickyFooter);

  // On phones, a header ⋯ action opens this bottom action sheet instead of a
  // dropdown menu (bigger tap targets, best-practice mobile pattern).
  const [actionSheetItems, setActionSheetItems] =
    useState<HeaderActionMenuItem[] | null>(null);

  // ── Header search (debounced) — kept in the layout so pages stay toolbar-free ──
  const [searchValue, setSearchValue] = useState(
    headerSearch?.initialValue ?? ""
  );
  const onSearchRef = React.useRef(headerSearch?.onSearch);
  onSearchRef.current = headerSearch?.onSearch;

  /**
   * ⚠⚠ HAND THE HEADER BACK WHEN A NEW PAGE TAKES IT OVER (ROADMAP A2b #58).
   *
   * Done DURING RENDER — React's documented "adjust state when a prop changes" — rather than in an
   * effect, so the new page never paints a frame holding the previous page's keyword.
   * ⚠ `chromeOwnerId` and `headerSearch` arrive in the SAME published snapshot, so the value read
   * here is always the incoming page's own `initialValue`, never the outgoing one's. Reading the
   * route instead would be wrong twice over — see the `chromeOwnerId` prop note.
   * ⚠ Resetting `searchValue` is NOT by itself what hands the new page its opening `onSearch("")` —
   * when the outgoing box was already empty the value does not change and nothing re-runs. The
   * debounce below is keyed on `searchOwnerId` for exactly that reason; see its own note.
   */
  const [searchOwnerId, setSearchOwnerId] = useState(chromeOwnerId);

  if (searchOwnerId !== chromeOwnerId) {
    setSearchOwnerId(chromeOwnerId);
    setSearchValue(headerSearch?.initialValue ?? "");
    setActionSheetItems(null);
  }

  // The header owns the search input and the overflow (⋯) menu, so it also owns
  // their keyboard shortcuts — every page that provides them gets these for free:
  //   Ctrl/Cmd+F → focus search   ·   F9 → open the overflow menu
  const searchInputRef = React.useRef<HTMLInputElement>(null);
  const menuTriggerRef = React.useRef<HTMLButtonElement>(null);

  // The first header action that is a dropdown menu — F9 opens this one.
  const firstMenuActionIndex = React.useMemo(
    () => mobileHeaderActions?.findIndex((a) => a.menuItems && a.menuItems.length > 0) ?? -1,
    [mobileHeaderActions]
  );

  const hasHeaderSearch = !!headerSearch;
  const hasHeaderMenu = firstMenuActionIndex >= 0;

  const headerShortcuts = React.useMemo(() => {
    const list = [];

    if (hasHeaderSearch) {
      list.push({
        key: "f",
        ctrl: true,
        action: () => searchInputRef.current?.focus(),
        description: "Focus search",
        preventDefault: true,
      });
    }

    if (hasHeaderMenu) {
      list.push({
        key: "F9",
        action: () => menuTriggerRef.current?.click(),
        description: "Open actions menu",
        preventDefault: true,
      });
    }

    return list;
  }, [hasHeaderSearch, hasHeaderMenu]);

  // ⌘F / F9 belong to the PAGE — not while a drawer, modal or the palette is open over it.
  useKeyboardShortcuts({ shortcuts: headerShortcuts, scope: "dashboard-header", onlyWhenNothingOpen: true });

  /**
   * ⚠⚠ KEYED ON THE PUBLISHER AS WELL AS THE KEYWORD (ROADMAP A2b #58), AND IT FIRES ONLY ON A REAL
   * CHANGE.
   *
   * ⓘ HISTORY, because the shape of this effect has now been wrong in both directions. The shell
   * used to be MOUNTED FRESH per page, so it always ran once on arrival and handed the new page
   * `onSearch("")`; several pages answered that by RUNNING THEIR LIST QUERY. Hoisting the shell
   * (#58) removed that call whenever the outgoing box was already empty, so `searchOwnerId` was
   * added to the deps to restore it — and that restored a REDUNDANT one on every page whose
   * provider already auto-loads. Both loads send the same filter and get the same rows.
   * ▶ The rule now: deliver a keyword when it CHANGES, or when a page arrives CARRYING one. An
   * arrival with an empty box is not a search. See `deliveredRef` below.
   */
  /**
   * ⚠⚠ WHAT WE DELIVERED LAST, AND TO WHOM. The effect above used to fire on EVERY owner change,
   * so arriving at a page with an empty box handed it `onSearch("")` 300ms later — a SECOND full
   * list query, with the same filter and the same rows as the provider's own `autoLoad` on mount.
   * Invisible with rows on screen; on an EMPTY list it read skeleton → "none" → skeleton → "none"
   * (owner, 2026-09-28, on /events/:eventId/tasks). ▶ ROADMAP "Known bugs / correctness".
   */
  const deliveredRef = React.useRef<{ ownerId: number | undefined; keyword: string } | null>(null);

  React.useEffect(() => {
    if (!onSearchRef.current) {
      return undefined;
    }

    const keyword = searchValue.trim();
    const delivered = deliveredRef.current;
    const sameOwner = !!delivered && delivered.ownerId === searchOwnerId;

    /**
     * ⚠⚠ AN ARRIVAL WITH AN EMPTY BOX IS NOT A SEARCH, SO IT IS NOT DELIVERED. The page's own
     * loader owns its first read — every page under this shell either mounts an auto-loading
     * provider or loads in a mount effect of its own, which `npm run check-app-shell` holds in
     * place. ⚠ A page that arrives CARRYING a keyword still gets it: that one IS a search, and
     * without it the page would show unfiltered rows under a filled-in box.
     */
    if (!sameOwner && keyword === '') {
      deliveredRef.current = { ownerId: searchOwnerId, keyword };

      return undefined;
    }

    // Nothing changed for this page — a re-render must not re-run its query.
    if (sameOwner && delivered?.keyword === keyword) {
      return undefined;
    }

    const id = setTimeout(() => {
      deliveredRef.current = { ownerId: searchOwnerId, keyword };
      onSearchRef.current?.(keyword);
    }, 300);

    return () => clearTimeout(id);
  }, [searchValue, searchOwnerId]);

  // Desktop sidebar state
  // The sidebar is hidden by default below the desktop (md) breakpoint — i.e.
  // on tablets — so the header/content get the full width. It stays openable via
  // the header hamburger. Initialized synchronously to avoid a first-paint flash.
  const belowDesktopInitial =
    typeof window !== "undefined" && theme.breakpoints?.md
      ? window.matchMedia(`(max-width: ${theme.breakpoints.md})`).matches
      : false;
  const [
    desktopCollapsed,
    { toggle: toggleDesktopSidebar, open: collapseSidebar, close: expandSidebar },
  ] = useDisclosure(belowDesktopInitial);

  // Keep the persistent sidebar collapsed below desktop (so it neither pushes
  // nor renders behind the tablet drawer) and expanded on desktop.
  const isDesktopWidth = useMediaQuery(`(min-width: ${theme.breakpoints.md})`);
  React.useEffect(() => {
    if (isDesktopWidth === undefined) return;
    if (isDesktopWidth) expandSidebar();
    else collapseSidebar();
  }, [isDesktopWidth, expandSidebar, collapseSidebar]);

  // Tablet navigation lives in a left Drawer (fixed width, backdrop, close
  // button). Only opened on tablet — desktop uses the persistent navbar and
  // phones use the BottomTabBar.
  const [tabletNavOpened, { open: openTabletNav, close: closeTabletNav }] =
    useDisclosure(false);
  const isTabletWidth = !isMobile && isDesktopWidth === false;

  // Mobile "More" view state
  const [isMoreOpen, setIsMoreOpen] = useState(false);
  const [isMoreClosing, setIsMoreClosing] = useState(false);

  // ⓘ Declared above the effect below, which lists `location.pathname` (#129: it listed `window.location.pathname`,
  // which React cannot watch — it re-ran only when something else re-rendered the layout).
  const location = useLocation();

  // Close More view when the route changes (not immediately on click).
  // This prevents a flash of the previous page while the new one loads.
  const pathnameRef = React.useRef(location.pathname);

  React.useEffect(() => {
    if (pathnameRef.current !== location.pathname) {
      pathnameRef.current = location.pathname;

      if (isMoreOpen) {
        // Start fade-out, then fully close after transition
        setIsMoreClosing(true);
        const timer = setTimeout(() => {
          setIsMoreOpen(false);
          setIsMoreClosing(false);
        }, 200);
        return () => clearTimeout(timer);
      }
    }

    return undefined;
  }, [location.pathname, isMoreOpen]);

  // Scroll position for mobile header border reveal
  const [scroll] = useWindowScroll();
  const isScrolled = scroll.y > 10;

  // Support drawer state
  const [
    supportDrawerOpened,
    { open: openSupportDrawer, close: closeSupportDrawer },
  ] = useDisclosure(false);

  const navigate = useNavigate();

  // Get filters from context to preserve them during navigation
  const { filters } = useEntityFiltersContext();

  /**
   * ⚠ CLOSE THE TABLET NAV DRAWER ON ANY NAVIGATION (ROADMAP A2b #58).
   *
   * `handleNavClick` already closes it when the host picks an item from the drawer itself — but a
   * navigation can also come from the ⌘K palette, a link inside the page, or the browser's Back
   * button, and this component used to be destroyed by all three. Now that it survives them, the
   * drawer would be left sitting over the page the host just moved to.
   */
  React.useEffect(() => {
    closeTabletNav();
  }, [location.pathname, closeTabletNav]);

  const {
    user,
    userRoles,
    userRolesNames,
    getVisibleRoutes,
    signOut,
    subscriptionStatus,
    trialEndsAt,
  } = useApp();

  const { reset: resetTags } = useTagsContext();
  const { reset: resetAccountPreference } = useAccountPreferenceContext();

  const allNavigationItems = getVisibleRoutes();

  // Which workspace are we in? Swap the sidebar between account and event nav.
  const activeEventId = matchEventId(location.pathname);
  const inEventWorkspace = !!activeEventId;
  // A stall's voucher redeemer has ONE page (2026-10-18): anything else sends them to it, and the strip's ways out
  // (the event's dashboard, crew messages, All events) are not drawn.
  const redeemer = useRedeemerOnly();
  const redeemerTo = redeemer.redeemerOnly ? redeemerRedirect(location.pathname, redeemer.eventId) : null;

  /**
   * ROADMAP A2b #63 — what the menu knows about this event's features. ⚠ Only for the event whose workspace is OPEN:
   * `currentEvent` still holds the previous event for a moment after a switch, and its features must not decide the
   * new event's menu. ⚠ No premium marks in crew mode — `hasFeature` answers for the crew member's OWN account, not
   * for the account that owns the event.
   */
  const featureNavCtx = useMemo((): EventFeatureNavCtx | null => {
    if (!inEventWorkspace || !currentEvent || currentEvent.id !== activeEventId) {
      return null;
    }

    return {
      on: currentEvent.featuresOn,
      usage: featureUsage,
      isLocked: isCrewMode ? undefined : (code: string) => !hasFeature(code),
    };
  }, [inEventWorkspace, currentEvent, activeEventId, featureUsage, isCrewMode, hasFeature]);

  const navigationItems = useMemo(() => {
    const wantScope = inEventWorkspace ? "event" : "account";
    const scoped = allNavigationItems
      .filter((item: SiteMapItem) => (item.scope ?? "account") === wantScope)
      .map((item: SiteMapItem) =>
        inEventWorkspace ? withEventId(item, activeEventId!) : item
      );

    // The event workspace also honours each item's dynamic `visibleWhen` (e.g.
    // ticketing pages only for a ticketed event). MENU-ONLY — routes stay
    // registered from the raw siteMap, so deep links still resolve.
    // ⚠ Features LAST, over what the role filter left: crew see the same menu as the owner, then their role's cut of it.
    const conditioned = inEventWorkspace
      ? markEventFeatureNav(
          filterNavByVisibility(scoped, {
            event: currentEvent
              ? { eventMode: currentEvent.eventMode, guestTracking: currentEvent.guestTracking }
              : null,
            // In crew mode the event nav is filtered by the caller's crew role.
            crewMode: isCrewMode,
            crewRoles,
            features: featureNavCtx,
          }),
          featureNavCtx,
        )
      : scoped;

    // In an event workspace, prepend a "back" entry. An owner goes back to all
    // their events; a cross-account crew member goes to their crew-events list. A
    // NON-account (password) crew session has a single event and no list, so it
    // gets no back-link (they sign out from the account menu).
    const isPasswordCrew = CrewSessionService.hasSession();
    if (inEventWorkspace && !(isCrewMode && isPasswordCrew)) {
      return [
        {
          name: isCrewMode ? "My Crew Events" : "All Events",
          subtitle: isCrewMode ? "Events you help run" : "Back to your events",
          url: isCrewMode ? "/crew" : "/events/all",
          icon: "ArrowLeft",
          visible: true,
          items: [],
        } as SiteMapItem,
        ...conditioned,
      ];
    }
    return conditioned;
  }, [allNavigationItems, inEventWorkspace, activeEventId, currentEvent, isCrewMode, crewRoles, featureNavCtx]);

  /**
   * ROADMAP A2b #63 — ⚠ A PAGE BEHIND "SHOW MORE" MAY HAVE JUST GOT ITS FIRST ROW. Leaving it re-asks which features
   * have data, so a budget the host started from "Show more" joins the menu instead of staying tucked away until the
   * next reload. Only on the way OUT of such a page, and only within the same event (a new event is asked anyway).
   */
  const leftMorePageRef = useRef<{ path: string; eventId: string } | null>(null);

  useEffect(() => {
    const previous = leftMorePageRef.current;

    if (previous && previous.path !== location.pathname && previous.eventId === activeEventId) {
      refreshFeatureUsage();
    }

    const top = activeNavItem(navigationItems, location.pathname);
    const page = top ? activeNavItem(top.items ?? [], location.pathname) : null;

    leftMorePageRef.current = activeEventId && (top?.navMore || page?.navMore) ? { path: location.pathname, eventId: activeEventId } : null;
  }, [location.pathname, navigationItems, activeEventId, refreshFeatureUsage]);

  const { name, subtitle, options } = page;
  const { translationKey, icon, iconColor } = options || {};

  // In crew mode gate the current page by the crew role (their effective role for
  // this event); otherwise by the account roles. Keeps the "no access" panel honest.
  const hasAccess = canUserAccess(page.roles, isCrewMode ? crewRoles : userRoles);

  // Resolve page icon color
  const pageIconColor = iconColor || DEFAULT_ICON_COLOR;

  // App version
  const appVersion = `v${import.meta.env.VITE_APP_VERSION}`;

  // Is user an owner?

  // ============================================
  // Translation Helpers
  // ============================================

  const getTranslatedName = useCallback(
    (itemTranslationKey?: string, fallbackName?: string): string => {
      if (itemTranslationKey) {
        const translated = t(`common:pages.${itemTranslationKey}.name`, {
          defaultValue: "",
        });
        if (translated) return translated;
      }
      return fallbackName || "";
    },
    [t]
  );

  const getTranslatedSubtitle = useCallback(
    (itemTranslationKey?: string, fallbackSubtitle?: string): string => {
      if (itemTranslationKey) {
        const translated = t(`common:pages.${itemTranslationKey}.subtitle`, {
          defaultValue: "",
          // A2e #101 — `{{brand}}` in a page subtitle is the brand from the white label.
          brand: brandName,
        });
        if (translated) return translated;
      }
      return fallbackSubtitle || "";
    },
    [t, brandName]
  );

  // Get translated current page name and subtitle
  const translatedName = getTranslatedName(translationKey, name);
  const translatedSubtitle = getTranslatedSubtitle(translationKey, subtitle);

  // A page may override the header title/subtitle (shorter title to free space
  // for actions; a live count in place of the static subtitle). `??` lets a page
  // pass "" to hide the subtitle.
  const displayTitle = headerTitle ?? translatedName;
  const displaySubtitle = headerSubtitle ?? translatedSubtitle;

  // Stable animation key — use pathname so transitions trigger on route changes
  const titleAnimationKey = location.pathname;

  // ============================================
  // Navigation Handlers
  // ============================================

  const handleNavClick = useCallback(
    (path: string) => {
      // Special sentinel for support drawer
      if (path === "__support__") {
        setIsMoreOpen(false);
        openSupportDrawer();
        return;
      }

      const targetPath = buildUrlWithFilters(path, filters);
      navigate(targetPath);

      // The tablet nav is a Drawer — close it after navigating.
      closeTabletNav();

      // Don't close More view here — useEffect on location.pathname
      // will close it once the new page actually renders, avoiding
      // a flash of the previous page content.
    },
    [filters, navigate, openSupportDrawer, closeTabletNav]
  );

  // Hamburger behavior depends on width: on tablet it opens the nav Drawer; on
  // desktop it collapses/expands the persistent sidebar.
  const handleToggleSidebar = useCallback(() => {
    if (isDesktopWidth === false) openTabletNav();
    else toggleDesktopSidebar();
  }, [isDesktopWidth, openTabletNav, toggleDesktopSidebar]);

  const handleSignOut = useCallback(() => {
    setIsMoreOpen(false);
    resetTags();
    resetAccountPreference();
    signOut();
    navigate("/account/signin");
  }, [
    resetTags,
    resetAccountPreference,
    signOut,
    navigate,
  ]);

  // ============================================
  // Bottom Tab Bar Handlers
  // ============================================

  const handleTabNavigate = useCallback(
    (path: string) => {
      // If More is open and user taps a tab, close More immediately
      // since we're navigating away from More to a primary tab.
      setIsMoreOpen(false);
      setIsMoreClosing(false);
      const targetPath = buildUrlWithFilters(path, filters);
      navigate(targetPath);
    },
    [filters, navigate]
  );

  const handleMoreClick = useCallback(() => {
    setIsMoreOpen((prev) => !prev);
  }, []);

  const handleCloseMore = useCallback(() => {
    setIsMoreClosing(true);
    const timer = setTimeout(() => {
      setIsMoreOpen(false);
      setIsMoreClosing(false);
    }, 200);
    return () => clearTimeout(timer);
  }, []);

  // ============================================
  // Subscription Footer
  // ============================================

  const { hasFooter } = useSubscriptionFooter({
    subscriptionStatus,
    trialEndsAt,
    showFooterMessage,
  });

  // ============================================
  // Navigation Helpers
  // ============================================

  const isItemActive = useCallback(
    (item: SiteMapItem): boolean => {
      if (item.url && location.pathname === item.url) return true;
      if (item.items?.length) return item.items.some(isItemActive);
      return false;
    },
    [location.pathname]
  );

  // Items for the mobile "More" menu. Everything that isn't a bottom-bar tab
  // belongs here — INCLUDING the children of bottom-bar parents (e.g. Tags /
  // Groups under Audience, or Templates under Events). Those parents are kept so
  // MoreView can list their child pages; only childless bottom-bar leaves (Home,
  // Analytics) are dropped since the bar already covers them.
  const moreViewItems = useMemo(() => {
    return navigationItems.filter((item: any) => {
      const hasVisibleChildren =
        Array.isArray(item.items) &&
        item.items.some((child: any) => child.visible !== false);
      return !item.bottomNavBar || hasVisibleChildren;
    });
  }, [navigationItems]);

  // Desktop sidebar needs shouldShowSidebarContent
  const shouldShowSidebarContent = !isMobile && !desktopCollapsed;

  const userInitials =
    user?.username
      ?.split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase() || "?";

  // Shared header search input (one instance mounts per breakpoint).
  const searchInput = headerSearch ? (
    <TextInput
      ref={searchInputRef}
      value={searchValue}
      onChange={(e) => setSearchValue(e.currentTarget.value)}
      placeholder={
        headerSearch.placeholder ||
        t("common:actions.search", { defaultValue: "Search..." })
      }
      leftSection={<AppIcon iconName="Search" size={16} />}
      rightSection={
        searchValue ? (
          <ActionIcon
            variant="subtle"
            color="neutral"
            size="md"
            onClick={() => setSearchValue("")}
            aria-label={t("common:buttons.clear", { defaultValue: "Clear" })}
          >
            <AppIcon iconName="X" size={14} />
          </ActionIcon>
        ) : null
      }
      size="md"
      radius="md"
      w="100%"
      aria-label={headerSearch.placeholder || "Search"}
    />
  ) : null;

  // ============================================
  // Render
  // ============================================

  return (
    <>
      <AppShell
        navbar={{
          width: desktopCollapsed ? 0 : SIDEBAR_WIDTH,
          // Below `md` (tablet + phone) the persistent navbar is always collapsed:
          // phones use the BottomTabBar and tablets use the left Drawer below.
          // Only desktop shows the pushing navbar.
          breakpoint: "md",
          collapsed: {
            mobile: true,
            desktop: desktopCollapsed,
          },
        }}
        footer={
          !isMobile && (hasStickyFooter || hasFooter)
            ? { height: hasStickyFooter ? 68 : { base: 90, sm: 60 } }
            : undefined
        }
        padding={0}
        styles={{
          main: {
            backgroundColor: theme.colors.neutral[0],
            display: "flex",
            flexDirection: "column",
            minHeight: "100vh",
            paddingTop: isMobile ? 60 : 0,
            // Reserve room for a fixed footer so page content can scroll fully
            // clear of it (the hardcoded 0 below would otherwise let content hide
            // behind the pinned Save bar).
            paddingBottom: isMobile
              ? // Reserve the tab bar, plus the save bar when it's showing, so
                // page content can scroll fully clear of both.
                (screenFocused ? 0 : BOTTOM_TAB_BAR_OFFSET) + (hasStickyFooter ? MOBILE_STICKY_FOOTER_HEIGHT : 0)
              : hasStickyFooter
                ? MOBILE_STICKY_FOOTER_HEIGHT + 16
                : 0,
          },
          navbar: {
            borderRight: `1px solid ${theme.colors.neutral[2]}`,
            backgroundColor: theme.colors.neutral[0],
          },
          footer: {
            borderTop: `1px solid ${theme.colors.neutral[2]}`,
          },
        }}
      >
        {/* Mobile Header */}
        <Box
          pos="fixed"
          top={0}
          left={0}
          right={0}
          h={60}
          bg="neutral.0"
          hiddenFrom="sm"
          style={{
            borderBottom: isScrolled
              ? `1px solid ${theme.colors.neutral[2]}`
              : "1px solid transparent",
            zIndex: 100,
            transition: "border-color 200ms ease",
          }}
        >
          <Group h="100%" px="md" justify="space-between">
            {/* Animated mobile title */}
            <Box style={{ flex: 1, minWidth: 0, overflow: "hidden", position: "relative" }}>
              <AnimatePresence mode="wait">
                <motion.div
                  key={isMoreOpen ? "__more__" : titleAnimationKey}
                  variants={titleVariants}
                  initial="initial"
                  animate="animate"
                  exit="exit"
                >
                  <Title order={3} c="neutral.9" style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {isMoreOpen
                      ? t("navigation.more", { defaultValue: "More" })
                      : displayTitle}
                  </Title>
                </motion.div>
              </AnimatePresence>
            </Box>

            <Group gap="md" wrap="nowrap" style={{ flexShrink: 0 }}>
              {/* The notification bell (A2b #57). ⚠ It renders NOTHING when there is no provider —
                  a password-crew session has no account user and therefore no bell, and the shell
                  must work for them rather than throwing. */}
              {!isMoreOpen && <NotificationBell />}

              {/* Filters button (mobile only) — badge shows applied count, opens
                  the page's filter drawer. Sits before the page actions. */}
              {!isMoreOpen && headerFilter && (
                <Indicator
                  label={headerFilter.activeCount || undefined}
                  disabled={!headerFilter.activeCount}
                  color="brand"
                  size={16}
                  offset={4}
                >
                  <ActionIcon
                    variant="default"
                    size={40}
                    radius="md"
                    onClick={headerFilter.onOpen}
                    aria-label={headerFilter.label || t("common:filters.title", { defaultValue: "Filters" })}
                    style={{ border: `1px solid ${theme.colors.neutral[2]}` }}
                  >
                    <AppIcon iconName="Filter" size={26} />
                  </ActionIcon>
                </Indicator>
              )}

              {/* Page-specific action buttons. A menu action opens a bottom sheet
                  (not a dropdown) on phones. */}
              {!isMoreOpen &&
                mobileHeaderActions?.map((action, index) => {
                  const isMenu = !!action.menuItems;

                  const button = (
                    <ActionIcon
                      ref={
                        isMenu && index === firstMenuActionIndex
                          ? menuTriggerRef
                          : undefined
                      }
                      variant={action.variant || "default"}
                      size={40}
                      radius="md"
                      disabled={action.disabled}
                      onClick={
                        isMenu
                          ? () => setActionSheetItems(action.menuItems ?? null)
                          : action.onClick
                      }
                      color={action.variant === "filled" ? "brand" : undefined}
                      style={{
                        border:
                          action.variant === "default" || !action.variant
                            ? `1px solid ${theme.colors.neutral[2]}`
                            : undefined,
                      }}
                    >
                      <AppIcon iconName={action.iconName} size={32} />
                    </ActionIcon>
                  );

                  return (
                    <Tooltip
                      key={`header-action-${index}`}
                      label={action.tooltip}
                      disabled={!action.tooltip}
                      position="bottom"
                      withArrow
                      openDelay={500}
                    >
                      {button}
                    </Tooltip>
                  );
                })}
            </Group>
          </Group>
        </Box>

        {/* Desktop Sidebar */}
        <AppShell.Navbar>
          {shouldShowSidebarContent && (
            <DesktopSidebar
              navigationItems={navigationItems}
              user={user}
              userInitials={userInitials}
              appVersion={appVersion}
              currentPath={location.pathname}
              onNavClick={handleNavClick}
              onToggleCollapse={toggleDesktopSidebar}
              onSignOut={handleSignOut}
              onOpenSupportDrawer={openSupportDrawer}
              getTranslatedName={getTranslatedName}
              isItemActive={isItemActive}
              t={t}
            />
          )}
        </AppShell.Navbar>

        {/* Desktop Footer — a page sticky action bar takes precedence when present. */}
        {!isMobile && hasStickyFooter && (
          <AppShell.Footer>
            {/* Offset the content by the sidebar width so the bar visually lives in
                the main area (the footer bar itself is full-width, like the app's
                other footers), with padding matching the page content. */}
            <Box
              style={{
                display: "flex",
                alignItems: "center",
                height: "100%",
                paddingRight: "var(--mantine-spacing-xl)",
                paddingLeft: desktopCollapsed
                  ? "var(--mantine-spacing-xl)"
                  : `calc(${SIDEBAR_WIDTH}px + var(--mantine-spacing-xl))`,
              }}
            >
              {stickyFooter}
            </Box>
          </AppShell.Footer>
        )}
        {!isMobile && hasFooter && !hasStickyFooter && (
          <AppShell.Footer>
            <SubscriptionFooter
              subscriptionStatus={subscriptionStatus}
              trialEndsAt={trialEndsAt}
              footerMessage={footerMessage}
              footerMessageType={footerMessageType}
              showFooterMessage={showFooterMessage}
            />
          </AppShell.Footer>
        )}

        {/* Mobile: float the sticky action bar directly ABOVE the bottom tab bar
            (not over it) so the tabs stay visible and tappable while there are
            unsaved changes. */}
        {isMobile && hasStickyFooter && (
          <Box
            pos="fixed"
            left={0}
            right={0}
            bottom={`calc(env(safe-area-inset-bottom, 0px) + ${BOTTOM_TAB_BAR_HEIGHT}px)`}
            bg="neutral.0"
            px="md"
            py="xs"
            style={{
              borderTop: `1px solid ${theme.colors.neutral[2]}`,
              boxShadow: theme.shadows.sm,
              zIndex: 99,
              display: "flex",
              alignItems: "center",
            }}
          >
            {stickyFooter}
          </Box>
        )}

        {/* Main Content */}
        <AppShell.Main>
          {/* Event context — a slim, always-visible strip telling you which event
              you're in (works on every screen size); tap it to jump to the hub. */}
          {currentEvent && !screenFocused && (
            <Box
              px={{ base: "md", sm: "xl" }}
              py={6}
              bg="brand.0"
              style={{ borderBottom: `1px solid ${theme.colors.brand[1]}` }}
            >
              <Group gap="xs" wrap="nowrap" justify="space-between">
                <UnstyledButton
                  onClick={() => (redeemer.redeemerOnly ? undefined : navigate(`/events/${currentEvent.id}`))}
                  disabled={redeemer.redeemerOnly}
                  style={{ flex: 1, minWidth: 0, display: "block" }}
                  aria-label={t("common:currentEvent", { defaultValue: "Current event" })}
                >
                  <Group gap={6} wrap="nowrap" style={{ minWidth: 0 }}>
                    <Text span style={{ fontSize: 15, flexShrink: 0 }} aria-hidden>
                      {currentEvent.emoji}
                    </Text>
                    <Text size="md" fw={600} c="brand.7" truncate>
                      {currentEvent.title}
                    </Text>
                    {redeemer.redeemerOnly ? null : <AppIcon iconName="ChevronRight" size={13} />}
                  </Group>
                </UnstyledButton>

                <Group gap="xs" wrap="nowrap" style={{ flexShrink: 0 }}>
                  {/* Crew messages — a private chat between the account's crew about this event. Lives in the
                      always-visible event strip so it's reachable from every event page, on every screen size
                      (a top strip → never blocks content the way a fixed bottom button would). */}
                  {redeemer.redeemerOnly ? null : <CrewMessagesLauncher />}

                  {/* Explicit way OUT of the event context — on mobile it's otherwise
                      hard to tell how to leave the event and get back to all events. */}
                  {redeemer.redeemerOnly ? null : (
                  <Button
                    variant="subtle"
                    color="brand"
                    size="compact-sm"
                    onClick={() => navigate("/events/all")}
                    leftSection={<AppIcon iconName="ArrowLeft" size={14} />}
                    style={{ flexShrink: 0 }}
                  >
                    {t("events:allEvents", { defaultValue: "All events" })}
                  </Button>
                  )}
                </Group>
              </Group>
            </Box>
          )}

          <PWAInstallBanner />
          <PWAUpdateBanner />

          {/* Mobile header search — a slim bar under the fixed top header.
              (The desktop header hosts the search on larger screens.) */}
          {searchInput && isMobile && !isMoreOpen && (
            <Box px="md" pt="sm" bg="neutral.0">
              {searchInput}
            </Box>
          )}

          {/* Page Header - Desktop */}
          {!isMobile && (
            <Box
              px={{ base: "md", sm: "xl" }}
              mih={HEADER_HEIGHT}
              bg="white"
              style={{
                borderBottom: `1px solid ${theme.colors.neutral[1]}`,
                display: "flex",
                alignItems: "center",
              }}
            >
              <Group justify="space-between" align="center" wrap="nowrap" w="100%">
                <Group gap="sm" align="center" style={{ flex: 1, minWidth: 0 }}>
                  {/* Sidebar control: opens the nav Drawer on tablet, collapses
                      the persistent sidebar on desktop. */}
                  <Tooltip
                    label={
                      isTabletWidth
                        ? "Open menu"
                        : desktopCollapsed
                          ? "Expand sidebar"
                          : "Collapse sidebar"
                    }
                    position="right"
                    withArrow
                  >
                    <ActionIcon
                      variant="subtle"
                      color="neutral"
                      size="lg"
                      onClick={handleToggleSidebar}
                      aria-label="Toggle sidebar"
                      style={{ flexShrink: 0 }}
                    >
                      <AppIcon iconName="Menu" size={20} />
                    </ActionIcon>
                  </Tooltip>

                  {/* Animated desktop page icon */}
                  <AnimatePresence mode="wait">
                    {icon && (
                      <motion.div
                        key={`icon-${titleAnimationKey}`}
                        initial={{ opacity: 0, scale: 0.85, y: -8 }}
                        animate={{
                          opacity: 1,
                          scale: 1,
                          y: 0,
                          transition: { duration: 0.3, ease: titleEase },
                        }}
                        exit={{
                          opacity: 0,
                          scale: 0.85,
                          y: 8,
                          transition: { duration: 0.18, ease: titleEase },
                        }}
                      >
                        <Box
                          w={{ base: 38, sm: 44 }}
                          h={{ base: 38, sm: 44 }}
                          style={{
                            backgroundColor: getIconBgColor(pageIconColor),
                            borderRadius: theme.radius.md,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                          }}
                        >
                          <AppIcon
                            iconName={icon}
                            size={28}
                            color={pageIconColor}
                          />
                        </Box>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  {/* Animated desktop title + subtitle */}
                  <AnimatePresence mode="wait">
                    <motion.div
                      key={titleAnimationKey}
                      variants={titleVariants}
                      initial="initial"
                      animate="animate"
                      exit="exit"
                      style={{ minWidth: 0 }}
                    >
                      <Box>
                        <Title
                          order={1}
                          fz={{ base: "1.375rem", sm: "1.75rem" }}
                          fw={700}
                          c="neutral.8"
                          lh={1.2}
                        >
                          {displayTitle}
                        </Title>
                        {displaySubtitle && (
                          <Text size="md" c="neutral.5" mt={4} lh={1.4}>
                            {displaySubtitle}
                          </Text>
                        )}
                      </Box>
                    </motion.div>
                  </AnimatePresence>
                </Group>

                {/* Header actions — standard page tools live here (search,
                    add, export…), plus always-available notifications. */}
                <Group gap="xs" wrap="nowrap" style={{ flexShrink: 0 }}>
                  {searchInput && (
                    <Box w={{ sm: 200, md: 260, lg: 320 }}>{searchInput}</Box>
                  )}

                  {!isMoreOpen &&
                    mobileHeaderActions?.map((action, index) => {
                      const button = (
                        <ActionIcon
                          ref={
                            action.menuItems && index === firstMenuActionIndex
                              ? menuTriggerRef
                              : undefined
                          }
                          variant={action.variant === "filled" ? "filled" : "subtle"}
                          color={action.variant === "filled" ? "brand" : "neutral"}
                          size={40}
                          radius="md"
                          disabled={action.disabled}
                          onClick={action.menuItems ? undefined : action.onClick}
                          aria-label={action.tooltip || "Action"}
                        >
                          <AppIcon iconName={action.iconName} size={22} />
                        </ActionIcon>
                      );

                      if (action.menuItems) {
                        return (
                          <Menu
                            key={`hdr-action-${index}`}
                            position="bottom-end"
                            withArrow
                            shadow="md"
                          >
                            <Menu.Target>{button}</Menu.Target>
                            <HeaderActionMenuItems items={action.menuItems} />
                          </Menu>
                        );
                      }

                      return (
                        <Tooltip
                          key={`hdr-action-${index}`}
                          label={action.tooltip}
                          disabled={!action.tooltip}
                          position="bottom"
                          withArrow
                          openDelay={400}
                        >
                          {button}
                        </Tooltip>
                      );
                    })}

                  {/* ⚠ NO NOTIFICATIONS BELL — removed with the rest of the feature, ROADMAP A2b #57.
                      It carried an unread dot fed by another product's API and opened a "Coming soon"
                      page. A bell that advertises unread items and then shows nothing is worse than no
                      bell: it teaches a host that the indicator means nothing. It comes back with #57,
                      together with the page it opens. */}
                </Group>
              </Group>
            </Box>
          )}

          {/* The section's sibling pages, on phones only — the pages a host can reach from here
              without going back out through "More". Renders nothing on desktop (the sidebar already
              shows them), nothing while More is open, and nothing for a section that is a single
              page or too large to scan. See SectionTabs for why. */}
          {!isMoreOpen && !screenFocused && (
            <SectionTabs
              navigationItems={navigationItems}
              currentPath={location.pathname}
              onNavigate={handleNavClick}
              getTranslatedName={getTranslatedName}
            />
          )}

          {/* Page Content or More View */}
          {isMobile && isMoreOpen ? (
            <Box
              style={{
                opacity: isMoreClosing ? 0 : 1,
                transition: "opacity 200ms ease",
              }}
            >
              <MoreView
                navigationItems={moreViewItems}
                user={user}
                appVersion={appVersion}
                currentPath={location.pathname}
                onNavigate={handleNavClick}
                onClose={handleCloseMore}
                onSignOut={handleSignOut}
                getTranslatedName={getTranslatedName}
                getTranslatedSubtitle={getTranslatedSubtitle}
              />
            </Box>
          ) : (
            <Box flex={1} p={{ base: "md", sm: "xl" }} bg="neutral.0">
              {redeemerTo ? <Navigate to={redeemerTo} replace /> : !hasAccess ? <NoAccessMessage roleNames={userRolesNames} /> : children}
            </Box>
          )}
        </AppShell.Main>
      </AppShell>

      {/* Tablet Navigation Drawer — same width as the desktop sidebar, with a
          backdrop + close button. Never opens on desktop or mobile. */}
      <StackedDrawer
        opened={tabletNavOpened && isTabletWidth}
        onClose={closeTabletNav}
        position="left"
        size={SIDEBAR_WIDTH}
        withCloseButton={false}
        padding={0}
        overlayProps={{ backgroundOpacity: 0.35 }}
        transitionProps={{ transition: "slide-right", duration: 200 }}
        styles={{ body: { padding: 0, height: "100%" } }}
      >
        <Box style={{ position: "relative", height: "100%" }}>
          <Tooltip label="Close menu" position="left" withArrow>
            <ActionIcon
              onClick={closeTabletNav}
              variant="subtle"
              color="neutral"
              size="lg"
              radius="md"
              aria-label="Close menu"
              style={{ position: "absolute", top: 14, right: 12, zIndex: 3 }}
            >
              <AppIcon iconName="X" size={20} />
            </ActionIcon>
          </Tooltip>
          <DesktopSidebar
            navigationItems={navigationItems}
            user={user}
            userInitials={userInitials}
            appVersion={appVersion}
            currentPath={location.pathname}
            onNavClick={handleNavClick}
            onToggleCollapse={closeTabletNav}
            onSignOut={handleSignOut}
            onOpenSupportDrawer={openSupportDrawer}
            getTranslatedName={getTranslatedName}
            isItemActive={isItemActive}
            t={t}
          />
        </Box>
      </StackedDrawer>

      {/* Mobile action sheet — a ⋯ menu action opens here on phones (the generic ActionSheet). */}
      <ActionSheet
        opened={!!actionSheetItems}
        onClose={() => setActionSheetItems(null)}
        sections={[{ items: actionSheetItems ?? [] }]}
      />

      {/* Bottom Tab Bar - Mobile Only */}
      {isMobile && !screenFocused && (
        <BottomTabBar
          navigationItems={navigationItems}
          currentPath={location.pathname}
          onNavigate={handleTabNavigate}
          isMoreActive={isMoreOpen}
          onMoreClick={handleMoreClick}
          isItemActive={isItemActive}
          getTranslatedName={getTranslatedName}
        />
      )}

      {/* Support Drawer */}
      <SupportDrawer
        opened={supportDrawerOpened}
        onClose={closeSupportDrawer}
      />
    </>
  );
};